import React from 'react';
import { Card, Container, Row, Col } from 'reactstrap';
import Header from 'components/Headers/Header.js';
import io from 'socket.io-client';
import cred from '../../util/cred';
import { axiosGet, axiosPost } from '../../util/apiConfig';

class WhatsappChat extends React.Component {
  state = {
    conversations: [],
    loadingConversations: false,
    activeChat: null,
    messages: [],
    loadingMessages: false,
    newMessageText: '',
    searchQuery: '',
    isMinimized: false,
    isMaximized: false,
    searchResults: [],
    loadingSearchResults: false,
    isChatSearching: false,
    chatSearchQuery: '',
  };

  componentDidMount() {
    this.fetchConversations();

    // Initialize Socket.io connection dynamically using backend endpoint
    const socketUrl = cred.API_PATH.split('/api/v1/')[0] || 'http://localhost:4000';
    this.socket = io(socketUrl, { transports: ['websocket'] });

    this.socket.on('whatsapp_message', (message) => {
      const { activeChat } = this.state;
      const messageCustomerNumber = message.direction === 'inbound' ? message.from : message.to;

      if (activeChat && activeChat.customerNumber === messageCustomerNumber) {
        this.setState((prevState) => ({
          messages: [...prevState.messages, message]
        }), () => {
          this.scrollToBottom();
          if (message.direction === 'inbound') {
            axiosPost('message/whatsapp/mark-as-read', { mobile: activeChat.customerNumber })
              .then(() => {
                if (this.props.fetchUnreadCount) {
                  this.props.fetchUnreadCount();
                }
                this.fetchConversations();
              })
              .catch((err) => console.error('Error marking as read:', err));
          } else {
            this.fetchConversations();
          }
        });
      } else {
        this.fetchConversations();
      }
    });

    this.socket.on('whatsapp_status', (statusUpdate) => {
      const { activeChat } = this.state;
      if (activeChat && activeChat.customerNumber === statusUpdate.customerNumber) {
        this.setState((prevState) => ({
          messages: prevState.messages.map((m) =>
            m.messageSid === statusUpdate.messageSid ? { ...m, status: statusUpdate.status } : m
          )
        }));
      }
      this.fetchConversations();
    });
  }

  componentWillUnmount() {
    if (this.socket) {
      this.socket.disconnect();
    }
  }

  fetchConversations = () => {
    this.setState({ loadingConversations: true });
    axiosGet('message/whatsapp/conversations')
      .then((res) => {
        if (!res.isError) {
          this.setState({ conversations: res.data || [], loadingConversations: false });
        } else {
          this.setState({ loadingConversations: false });
        }
      })
      .catch((err) => {
        this.setState({ loadingConversations: false });
        console.error('Error fetching conversations:', err);
      });
  };

  searchLeadsBackend = (query) => {
    if (!query.trim()) {
      this.setState({ searchResults: [], loadingSearchResults: false });
      return;
    }
    this.setState({ loadingSearchResults: true });
    axiosGet(`message/whatsapp/search-leads?q=${encodeURIComponent(query)}`)
      .then((res) => {
        if (!res.isError) {
          // Filter out results that are already in active conversations to avoid duplicates, except message matches
          const activeNumbers = new Set((this.state.conversations || []).map(c => c.customerNumber));
          const filteredResults = (res.data || []).filter(item => !activeNumbers.has(item.customerNumber) || item.isMessageMatch);
          this.setState({ searchResults: filteredResults, loadingSearchResults: false });
        } else {
          this.setState({ loadingSearchResults: false });
        }
      })
      .catch((err) => {
        this.setState({ loadingSearchResults: false });
        console.error('Error searching leads:', err);
      });
  };

  handleSearchChange = (query) => {
    this.setState({ searchQuery: query });
    
    // Clear previous timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    if (query.trim()) {
      this.searchTimeout = setTimeout(() => {
        this.searchLeadsBackend(query);
      }, 350); // 350ms debounce
    } else {
      this.setState({ searchResults: [] });
    }
  };

  scrollToMessage = (messageId) => {
    const el = document.getElementById(`msg-${messageId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Add highlight class
      el.classList.add('highlight-flash');
      setTimeout(() => {
        el.classList.remove('highlight-flash');
      }, 2000);
    }
  };

  highlightText = (text, query) => {
    if (!query || !query.trim()) return text;
    const parts = (text || '').split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, i) => 
          part.toLowerCase() === query.toLowerCase() ? (
            <mark key={i} style={{ backgroundColor: '#ffe066', padding: '0 2px', borderRadius: '2px', color: '#111b21' }}>{part}</mark>
          ) : part
        )}
      </span>
    );
  };

  selectConversation = (chat) => {
    this.setState({ activeChat: chat, messages: [], loadingMessages: true, isChatSearching: false, chatSearchQuery: '' });
    axiosGet(`message/whatsapp/history/${chat.customerNumber}`)
      .then((res) => {
        if (!res.isError) {
          this.setState({ messages: res.data || [], loadingMessages: false }, () => {
            if (this.props.fetchUnreadCount) {
              this.props.fetchUnreadCount();
            }
            if (chat.isMessageMatch && chat.matchedMessageId) {
              setTimeout(() => {
                this.scrollToMessage(chat.matchedMessageId);
              }, 400);
            } else {
              this.scrollToBottom();
            }
          });
        } else {
          this.setState({ loadingMessages: false });
        }
      })
      .catch((err) => {
        this.setState({ loadingMessages: false });
        console.error('Error fetching history:', err);
      });
  };

  sendWhatsAppDirect = () => {
    const { activeChat, newMessageText } = this.state;
    if (!activeChat || !newMessageText.trim()) return;

    const payload = {
      to: activeChat.customerNumber,
      message: newMessageText,
    };

    axiosPost('message/whatsapp/send', payload)
      .then((res) => {
        if (!res.isError) {
          this.setState({ newMessageText: '' });
          const newMsg = res.data;
          this.setState((prevState) => {
            if (prevState.messages.find((m) => m.messageSid === newMsg.messageSid)) {
              return null;
            }
            return {
              messages: [...prevState.messages, newMsg]
            };
          }, () => {
            this.scrollToBottom();
            this.fetchConversations();
          });
        } else {
          alert('Failed to send message: ' + res.message);
        }
      })
      .catch((err) => {
        console.error('Error sending message:', err);
        alert('Failed to send message.');
      });
  };

  scrollToBottom = () => {
    if (this.messagesEnd) {
      this.messagesEnd.scrollIntoView({ behavior: 'smooth' });
    }
  };

  render() {
    const { conversations, loadingConversations, activeChat, messages, loadingMessages, newMessageText, searchQuery, isMinimized, isMaximized, searchResults, loadingSearchResults } = this.state;

    // Filter conversations based on search query
    const filteredConversations = (conversations || []).filter((c) => {
      const q = (searchQuery || '').toLowerCase().trim();
      if (!q) return true;
      const nameMatch = (c.name || '').toLowerCase().includes(q);
      const numberMatch = (c.customerNumber || '').toLowerCase().includes(q);
      const messageMatch = (c.lastMessage || '').toLowerCase().includes(q);
      return nameMatch || numberMatch || messageMatch;
    });

    return (
      <div className={`wa-mac-overlay ${isMinimized ? 'minimized' : ''} ${isMaximized ? 'maximized' : ''}`}>
        {/* Style block inside the page to encapsulate WA look */}
        <style>{`
          .wa-mac-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
            z-index: 9999;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: -apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", Helvetica, Arial, sans-serif;
            padding: 24px;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .wa-mac-overlay.minimized {
            top: auto !important;
            left: auto !important;
            right: 24px !important;
            bottom: 24px !important;
            width: 60px !important;
            height: 60px !important;
            background: transparent !important;
            padding: 0 !important;
            box-shadow: none !important;
            pointer-events: auto !important;
            border-radius: 50% !important;
          }
          .wa-mac-overlay.maximized {
            padding: 0 !important;
          }
          .wa-mac-window {
            width: 100%;
            height: 100%;
            max-width: 1280px;
            max-height: 880px;
            min-height: 550px;
            min-width: 850px;
            background: #ffffff;
            border-radius: 12px;
            box-shadow: 0 16px 45px rgba(0, 0, 0, 0.28);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            border: 1px solid rgba(0, 0, 0, 0.15);
            animation: waWindowAppear 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .wa-mac-window.maximized {
            max-width: 100% !important;
            max-height: 100% !important;
            width: 100vw !important;
            height: 100vh !important;
            border-radius: 0 !important;
            border: none !important;
          }
          @keyframes waWindowAppear {
            from {
              opacity: 0;
              transform: scale(0.96) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          @media (max-width: 1300px) or (max-height: 920px) {
            .wa-mac-overlay:not(.minimized) {
              padding: 0;
            }
            .wa-mac-window {
              max-width: 100%;
              max-height: 100%;
              border-radius: 0;
              border: none;
            }
          }
          .wa-mac-header-bar {
            background: #f6f6f6;
            height: 48px;
            display: flex;
            align-items: center;
            padding: 0 16px;
            border-bottom: 1px solid #e1e1e1;
            user-select: none;
            position: relative;
            flex-shrink: 0;
          }
          .wa-mac-traffic-lights {
            display: flex;
            gap: 8px;
            align-items: center;
            z-index: 10;
            padding: 4px;
          }
          .wa-mac-dot {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            display: inline-block;
            cursor: pointer;
            position: relative;
            box-sizing: border-box;
            transition: filter 0.1s ease;
          }
          .wa-mac-dot:hover {
            filter: brightness(0.85);
          }
          .wa-mac-close {
            background: #ff5f56;
            border: 0.5px solid #e0443e;
          }
          .wa-mac-minimize {
            background: #ffbd2e;
            border: 0.5px solid #dfa023;
          }
          .wa-mac-zoom {
            background: #27c93f;
            border: 0.5px solid #1aab29;
          }
          .wa-mac-traffic-lights:hover .wa-mac-close::before {
            content: "×";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 9px;
            color: #4c0002;
            font-weight: 700;
          }
          .wa-mac-traffic-lights:hover .wa-mac-minimize::before {
            content: "–";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) translateY(-0.5px);
            font-size: 9px;
            color: #5c3e00;
            font-weight: 700;
          }
          .wa-mac-traffic-lights:hover .wa-mac-zoom::before {
            content: "⤢";
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 8px;
            color: #005000;
            font-weight: 700;
          }
          .wa-minimized-badge {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            background: linear-gradient(135deg, #00c694, #00a884);
            display: flex;
            align-items: center;
            justify-content: center;
            color: #ffffff;
            font-size: 30px;
            cursor: pointer;
            box-shadow: 0 8px 24px rgba(0, 168, 132, 0.4);
            position: relative;
            transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
            animation: badgeFloat 3s ease-in-out infinite;
          }
          .wa-minimized-badge:hover {
            transform: scale(1.1) translateY(-3px);
            box-shadow: 0 12px 30px rgba(0, 168, 132, 0.6);
          }
          @keyframes badgeFloat {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-6px); }
            100% { transform: translateY(0px); }
          }
          .wa-pulse-ring {
            position: absolute;
            top: -2px; left: -2px; right: -2px; bottom: -2px;
            border-radius: 50%;
            border: 2px solid #00a884;
            animation: badgePulse 2s cubic-bezier(0.24, 0, 0.38, 1) infinite;
            pointer-events: none;
          }
          @keyframes badgePulse {
            0% {
              transform: scale(1);
              opacity: 0.8;
            }
            100% {
              transform: scale(1.4);
              opacity: 0;
            }
          }
          .wa-mac-title-text {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            font-size: 0.88rem;
            font-weight: 600;
            color: #333333;
            letter-spacing: 0.1px;
            display: flex;
            align-items: center;
          }
          .wa-mac-main-container {
            display: flex;
            flex: 1;
            overflow: hidden;
            height: calc(100% - 48px);
          }
          .wa-sidebar {
            width: 340px;
            background: #ffffff;
            border-right: 1px solid #e9edef;
            display: flex;
            flex-direction: column;
            height: 100%;
            flex-shrink: 0;
          }
          .wa-sidebar-header {
            background: #f6f6f6;
            padding: 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #e9edef;
          }
          .wa-sidebar-title {
            margin: 0;
            font-size: 1.1rem;
            font-weight: 700;
            color: #111b21;
          }
          .wa-search-container {
            padding: 8px 12px;
            background: #ffffff;
            border-bottom: 1px solid #f0f2f5;
          }
          .wa-search-wrapper {
            position: relative;
          }
          .wa-search-input {
            width: 100%;
            padding: 8px 12px 8px 34px;
            border: none;
            border-radius: 8px;
            font-size: 0.85rem;
            outline: none;
            background: #f0f2f5;
            color: #111b21;
            box-sizing: border-box;
          }
          .wa-search-icon {
            position: absolute;
            left: 12px;
            top: 50%;
            transform: translateY(-50%);
            color: #667781;
            font-size: 0.8rem;
          }
          .wa-list {
            flex: 1;
            overflow-y: auto;
          }
          .wa-list::-webkit-scrollbar {
            width: 6px;
          }
          .wa-list::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.12);
            border-radius: 3px;
          }
          .wa-item {
            display: flex;
            padding: 12px 16px;
            border-bottom: 1px solid #f0f2f5;
            cursor: pointer;
            transition: background 0.15s ease;
            align-items: center;
          }
          .wa-item:hover {
            background: #f5f6f6;
          }
          .wa-item.active {
            background: #eff2f5;
          }
          .wa-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #00a884;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 700;
            margin-right: 12px;
            font-size: 1rem;
            flex-shrink: 0;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .wa-details {
            flex: 1;
            min-width: 0;
          }
          .wa-details-top {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 4px;
          }
          .wa-name {
            font-weight: 600;
            font-size: 0.9rem;
            color: #111b21;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .wa-time {
            font-size: 0.72rem;
            color: #667781;
          }
          .wa-details-bottom {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .wa-msg {
            font-size: 0.8rem;
            color: #667781;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
          }
          .wa-chat-pane {
            flex: 1;
            display: flex;
            flex-direction: column;
            background-color: #efeae2;
            position: relative;
            height: 100%;
            min-width: 0;
          }
          .wa-chat-pane::before {
            content: "";
            position: absolute;
            top: 0; left: 0; right: 0; bottom: 0;
            background-image: url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png');
            opacity: 0.07;
            pointer-events: none;
            z-index: 1;
          }
          .wa-chat-header {
            background: rgba(246, 246, 246, 0.95);
            backdrop-filter: blur(10px);
            color: #111b21;
            padding: 10px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            z-index: 10;
            border-bottom: 1px solid #e9edef;
            box-shadow: 0 1px 2px rgba(0,0,0,0.03);
          }
          .wa-chat-body {
            flex: 1;
            overflow-y: auto;
            padding: 20px 24px;
            z-index: 5;
            display: flex;
            flex-direction: column;
          }
          .wa-chat-body::-webkit-scrollbar {
            width: 6px;
          }
          .wa-chat-body::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.15);
            border-radius: 3px;
          }
          .wa-bubble {
            max-width: 60%;
            padding: 8px 12px 6px 12px;
            border-radius: 8px;
            margin-bottom: 10px;
            font-size: 0.88rem;
            line-height: 1.45;
            position: relative;
            word-break: break-word;
            box-shadow: 0 1px 1px rgba(0,0,0,0.08);
            animation: bubbleSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          @keyframes bubbleSlideUp {
            from {
              opacity: 0;
              transform: translateY(12px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .wa-bubble.outbound {
            align-self: flex-end;
            background: #d9fdd3;
            border-top-right-radius: 2px;
          }
          .wa-bubble.inbound {
            align-self: flex-start;
            background: #ffffff;
            border-top-left-radius: 2px;
          }
          .wa-meta {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            margin-top: 4px;
            font-size: 0.68rem;
            color: #667781;
          }
          .wa-check {
            margin-left: 4px;
            display: inline-flex;
          }
          .wa-chat-footer {
            background: #f0f2f5;
            padding: 12px 16px;
            display: flex;
            align-items: center;
            z-index: 10;
            border-top: 1px solid #e9edef;
          }
          .wa-chat-input-wrapper {
            flex: 1;
            margin: 0 12px;
            position: relative;
          }
          .wa-chat-input {
            width: 100%;
            padding: 10px 16px;
            border: none;
            border-radius: 8px;
            outline: none;
            font-size: 0.9rem;
            background: #ffffff;
            color: #111b21;
            box-sizing: border-box;
          }
          .wa-send-btn {
            background: #00a884;
            color: white;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: transform 0.15s ease, background 0.15s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.15);
            flex-shrink: 0;
          }
          .wa-send-btn:hover {
            background: #008f70;
            transform: scale(1.05);
          }
          .wa-send-btn:disabled {
            background: #aebac1;
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
          .wa-no-chat {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: #667781;
            text-align: center;
            padding: 40px;
            z-index: 2;
            background: #f8f9fa;
            border-left: 1px solid #e9edef;
          }
          .wa-no-chat-title {
            font-size: 1.7rem;
            font-weight: 300;
            color: #41525d;
            margin-top: 20px;
            margin-bottom: 8px;
          }
          .wa-no-chat-subtitle {
            font-size: 0.88rem;
            color: #8696a0;
            max-width: 340px;
            line-height: 1.6;
          }
          .wa-section-header {
            font-size: 0.72rem;
            font-weight: 700;
            text-transform: uppercase;
            color: #00a884;
            padding: 12px 16px 6px 16px;
            background: #f8f9fa;
            letter-spacing: 0.5px;
            border-bottom: 1px solid #f0f2f5;
          }
          .wa-bubble.highlight-flash {
            animation: flashHighlight 2s ease;
          }
          @keyframes flashHighlight {
            0% {
              background: #ffe066 !important;
            }
            100% {
              background: inherit;
            }
          }
          .wa-chat-search-panel {
            width: 300px;
            background: #ffffff;
            border-left: 1px solid #e9edef;
            display: flex;
            flex-direction: column;
            height: 100%;
            flex-shrink: 0;
            animation: slideInRight 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
          }
          @keyframes slideInRight {
            from {
              transform: translateX(100%);
            }
            to {
              transform: translateX(0);
            }
          }
          .wa-chat-search-header {
            background: #f6f6f6;
            height: 48px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 16px;
            border-bottom: 1px solid #e9edef;
            flex-shrink: 0;
          }
          .wa-chat-search-input-container {
            padding: 8px 12px;
            background: #ffffff;
            border-bottom: 1px solid #f0f2f5;
            flex-shrink: 0;
          }
          .wa-chat-search-results {
            flex: 1;
            overflow-y: auto;
            background: #ffffff;
          }
          .wa-chat-search-result-item {
            padding: 12px 16px;
            border-bottom: 1px solid #f0f2f5;
            cursor: pointer;
            transition: background 0.15s ease;
          }
          .wa-chat-search-result-item:hover {
            background: #f5f6f6;
          }
          .wa-chat-search-result-meta {
            display: flex;
            justify-content: space-between;
            font-size: 0.72rem;
            color: #667781;
            margin-bottom: 4px;
          }
          .wa-chat-search-result-body {
            font-size: 0.8rem;
            color: #111b21;
            line-height: 1.4;
          }
        `}</style>
        
        {isMinimized ? (
          <div className="wa-minimized-badge" onClick={() => this.setState({ isMinimized: false })} title="Restore WhatsApp Chat">
            <i className="fab fa-whatsapp" />
            <div className="wa-pulse-ring" />
          </div>
        ) : (
          <div className={`wa-mac-window ${isMaximized ? 'maximized' : ''}`}>
            {/* macOS Title Bar with traffic lights */}
            <div className="wa-mac-header-bar">
              <div className="wa-mac-traffic-lights">
                <span className="wa-mac-dot wa-mac-close" onClick={() => this.props.history.push('/admin/index')} title="Close and return to Dashboard" />
                <span className="wa-mac-dot wa-mac-minimize" onClick={() => this.setState({ isMinimized: true })} title="Minimize" />
                <span className="wa-mac-dot wa-mac-zoom" onClick={() => this.setState({ isMaximized: !isMaximized })} title="Zoom" />
              </div>
              <div className="wa-mac-title-text">
                <i className="fab fa-whatsapp mr-1 text-success" /> WhatsApp
              </div>
            </div>

          <div className="wa-mac-main-container">
            
            <div className="wa-sidebar">
              <div className="wa-sidebar-header">
                <h4 className="wa-sidebar-title">Chats</h4>
              </div>
              <div className="wa-search-container">
                <div className="wa-search-wrapper">
                  <i className="fas fa-search wa-search-icon" />
                  <input
                    type="text"
                    placeholder="Search or start new chat"
                    className="wa-search-input"
                    value={searchQuery}
                    onChange={(e) => this.handleSearchChange(e.target.value)}
                  />
                </div>
              </div>

              <div className="wa-list">
                {loadingConversations ? (
                  <div className="text-center py-4 text-muted" style={{ fontSize: '0.88rem' }}>
                    <i className="fas fa-circle-notch fa-spin mr-2" /> Loading chats...
                  </div>
                ) : !searchQuery.trim() ? (
                  conversations.length === 0 ? (
                    <div className="text-center py-5 text-muted" style={{ fontSize: '0.88rem' }}>
                      No active conversations.
                    </div>
                  ) : (
                    conversations.map((chat, index) => {
                      const isActive = activeChat && activeChat.customerNumber === chat.customerNumber;
                      const dateStr = chat.timestamp ? new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                      const chatName = chat.name || chat.customerNumber || 'Customer';
                      const initials = chatName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'C';

                      return (
                        <div
                          key={index}
                          className={`wa-item ${isActive ? 'active' : ''}`}
                          onClick={() => this.selectConversation(chat)}
                        >
                          <div className="wa-avatar">{initials}</div>
                          <div className="wa-details">
                            <div className="wa-details-top">
                              <span className="wa-name">{chatName}</span>
                              <span className="wa-time">{dateStr}</span>
                            </div>
                            <div className="wa-details-bottom">
                              <span className="wa-msg">
                                {chat.direction === 'outbound' && <i className="fas fa-reply mr-1 text-muted" style={{ fontSize: '0.75rem' }} />}
                                {chat.lastMessage}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )
                ) : (
                  <>
                    {filteredConversations.length > 0 && (
                      <>
                        <div className="wa-section-header">Active Chats</div>
                        {filteredConversations.map((chat, index) => {
                          const isActive = activeChat && activeChat.customerNumber === chat.customerNumber;
                          const dateStr = chat.timestamp ? new Date(chat.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                          const chatName = chat.name || chat.customerNumber || 'Customer';
                          const initials = chatName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'C';

                          return (
                            <div
                              key={`active-${index}`}
                              className={`wa-item ${isActive ? 'active' : ''}`}
                              onClick={() => this.selectConversation(chat)}
                            >
                              <div className="wa-avatar">{initials}</div>
                              <div className="wa-details">
                                <div className="wa-details-top">
                                  <span className="wa-name">{this.highlightText(chatName, searchQuery)}</span>
                                  <span className="wa-time">{dateStr}</span>
                                </div>
                                <div className="wa-details-bottom">
                                  <span className="wa-msg">
                                    {chat.direction === 'outbound' && <i className="fas fa-reply mr-1 text-muted" style={{ fontSize: '0.75rem' }} />}
                                    {this.highlightText(chat.lastMessage, searchQuery)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}

                    <div className="wa-section-header">Global Leads / Conversations</div>
                    {loadingSearchResults ? (
                      <div className="text-center py-4 text-muted" style={{ fontSize: '0.88rem' }}>
                        <i className="fas fa-circle-notch fa-spin mr-2" /> Searching database...
                      </div>
                    ) : searchResults.length === 0 ? (
                      <div className="text-center py-4 text-muted" style={{ fontSize: '0.88rem' }}>
                        No leads or conversations found.
                      </div>
                    ) : (
                      searchResults.map((chat, index) => {
                        const isActive = activeChat && activeChat.customerNumber === chat.customerNumber;
                        const chatName = chat.name || 'Lead / Customer';
                        const initials = chatName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'L';

                        return (
                          <div
                            key={`global-${index}`}
                            className={`wa-item ${isActive ? 'active' : ''}`}
                            onClick={() => this.selectConversation(chat)}
                          >
                            <div className="wa-avatar" style={{ backgroundColor: '#008069' }}>{initials}</div>
                            <div className="wa-details">
                              <div className="wa-details-top">
                                <span className="wa-name">{this.highlightText(chatName, searchQuery)}</span>
                              </div>
                              <div className="wa-details-bottom">
                                <span className="wa-msg">
                                  {chat.isMessageMatch ? (
                                    <>
                                      {chat.direction === 'outbound' && <i className="fas fa-reply mr-1 text-muted" style={{ fontSize: '0.75rem' }} />}
                                      {this.highlightText(chat.lastMessage, searchQuery)}
                                    </>
                                  ) : (
                                    this.highlightText(chat.customerNumber.replace('whatsapp:', ''), searchQuery)
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </>
                )}
              </div>
            </div>

            {activeChat ? (
              <div className="wa-chat-pane">
                <div className="wa-chat-header">
                  <div className="d-flex align-items-center">
                    <div className="wa-avatar" style={{ width: '38px', height: '38px', fontSize: '0.95rem', background: '#008069' }}>
                      {(activeChat.name || activeChat.customerNumber || 'Customer').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'C'}
                    </div>
                    <div>
                      <h5 className="m-0 font-weight-bold" style={{ fontSize: '0.92rem' }}>{activeChat.name || activeChat.customerNumber}</h5>
                      <small className="text-muted" style={{ fontSize: '0.72rem' }}>{activeChat.customerNumber.replace('whatsapp:', '')}</small>
                    </div>
                  </div>
                  <div>
                    <button
                      className="btn btn-link text-muted p-2"
                      style={{ fontSize: '1.1rem', outline: 'none', boxShadow: 'none' }}
                      onClick={() => this.setState((prevState) => ({ isChatSearching: !prevState.isChatSearching }))}
                      title="Search messages"
                    >
                      <i className={`fas fa-search ${this.state.isChatSearching ? 'text-success' : ''}`} />
                    </button>
                  </div>
                </div>

                <div className="wa-chat-body">
                  {loadingMessages ? (
                    <div className="text-center py-5 text-muted m-auto">
                      <i className="fas fa-circle-notch fa-spin mr-2" /> Loading messages...
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-muted m-auto" style={{ fontSize: '0.88rem' }}>
                      No messages yet. Type a message below to start the conversation!
                    </div>
                  ) : (
                    messages.map((item, idx) => {
                      const isOutbound = item.direction === 'outbound';
                      const timeStr = new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

                      let statusIcon = null;
                      if (isOutbound) {
                        if (item.status === 'read') {
                          statusIcon = <i className="fas fa-check-double text-info" />;
                        } else if (item.status === 'delivered') {
                          statusIcon = <i className="fas fa-check-double text-muted" />;
                        } else if (item.status === 'sent') {
                          statusIcon = <i className="fas fa-check text-muted" />;
                        } else if (item.status === 'failed') {
                          statusIcon = <i className="fas fa-exclamation-circle text-danger" title={item.errorMessage} />;
                        } else {
                          statusIcon = <i className="fas fa-clock text-muted" />;
                        }
                      }

                      return (
                        <div
                          key={idx}
                          id={`msg-${item._id || idx}`}
                          className={`wa-bubble ${isOutbound ? 'outbound' : 'inbound'}`}
                        >
                          <div>{this.highlightText(item.body, this.state.chatSearchQuery || searchQuery)}</div>
                          <div className="wa-meta">
                            <span>{timeStr}</span>
                            {isOutbound && <span className="wa-check">{statusIcon}</span>}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={(el) => { this.messagesEnd = el; }} />
                </div>

                <div className="wa-chat-footer">
                  <div className="wa-chat-input-wrapper">
                    <input
                      type="text"
                      placeholder="Type a message..."
                      className="wa-chat-input"
                      value={newMessageText}
                      onChange={(e) => this.setState({ newMessageText: e.target.value })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          this.sendWhatsAppDirect();
                        }
                      }}
                    />
                  </div>
                  <button
                    className="wa-send-btn"
                    onClick={this.sendWhatsAppDirect}
                    disabled={!newMessageText.trim()}
                  >
                    <i className="fas fa-paper-plane" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="wa-no-chat">
                <i className="fab fa-whatsapp text-success" style={{ fontSize: '5rem', opacity: '0.4' }} />
                <h3 className="wa-no-chat-title">WhatsApp for macOS</h3>
                <p className="wa-no-chat-subtitle">
                  Select an active customer from the sidebar to view full history and exchange real-time WhatsApp messages.
                </p>
              </div>
            )}

            {activeChat && this.state.isChatSearching && (
              <div className="wa-chat-search-panel">
                <div className="wa-chat-search-header">
                  <span className="font-weight-bold" style={{ fontSize: '0.9rem', color: '#111b21' }}>Search Messages</span>
                  <button
                    className="btn btn-link text-muted p-1"
                    style={{ fontSize: '1rem', outline: 'none', boxShadow: 'none' }}
                    onClick={() => this.setState({ isChatSearching: false, chatSearchQuery: '' })}
                  >
                    <i className="fas fa-times" />
                  </button>
                </div>
                
                <div className="wa-chat-search-input-container">
                  <div className="wa-search-wrapper">
                    <i className="fas fa-search wa-search-icon" />
                    <input
                      type="text"
                      placeholder="Search..."
                      className="wa-chat-input"
                      value={this.state.chatSearchQuery}
                      onChange={(e) => this.setState({ chatSearchQuery: e.target.value })}
                    />
                  </div>
                </div>

                <div className="wa-chat-search-results">
                  {!this.state.chatSearchQuery.trim() ? (
                    <div className="text-center py-5 text-muted px-3" style={{ fontSize: '0.85rem' }}>
                      Search for messages with {activeChat.name || activeChat.customerNumber}.
                    </div>
                  ) : (() => {
                    const query = this.state.chatSearchQuery.toLowerCase().trim();
                    const filteredMessages = (messages || []).filter(item => 
                      (item.body || '').toLowerCase().includes(query)
                    );

                    if (filteredMessages.length === 0) {
                      return (
                        <div className="text-center py-5 text-muted px-3" style={{ fontSize: '0.85rem' }}>
                          No messages found
                        </div>
                      );
                    }

                    return filteredMessages.map((item, index) => {
                      const timeStr = new Date(item.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + new Date(item.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      const senderName = item.direction === 'outbound' ? 'You' : (activeChat.name || 'Customer');
                      
                      return (
                        <div
                          key={index}
                          className="wa-chat-search-result-item"
                          onClick={() => this.scrollToMessage(item._id || index)}
                        >
                          <div className="wa-chat-search-result-meta">
                            <span className="font-weight-bold" style={{ color: '#008069' }}>{senderName}</span>
                            <span>{timeStr}</span>
                          </div>
                          <div className="wa-chat-search-result-body">
                            {this.highlightText(item.body, this.state.chatSearchQuery)}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}

            </div>
          </div>
        )}
      </div>
    );
  }
}

export default WhatsappChat;
